import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  Textarea,
  VStack,
  HStack,
  Text,
  Badge,
  useToast,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  SimpleGrid,
  IconButton,
  Divider,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import axios from "axios";
import { ChatState } from "../Context/ChatProvider";
import { useNavigate } from "react-router-dom";

const VendorJobsPage = () => {
  const { user } = ChatState();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isViewOpen,
    onOpen: onViewOpen,
    onClose: onViewClose,
  } = useDisclosure();
  const toast = useToast();

  const [formData, setFormData] = useState({
    clientName: "",
    jobTitle: "",
    description: "",
    priority: "Medium",
    dueDate: "",
    location: "",
    estimatedCost: "",
    notes: "",
  });

  const priorities = ["Low", "Medium", "High"];

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const { data } = await axios.get("/api/jobs", config);
      setJobs(data);
    } catch (error) {
      toast({
        title: "Error fetching jobs",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user && user.role === "vendor") {
      if (user.isApproved === "pending") {
        navigate("/vendor-pending");
        return;
      }
      if (user.isApproved === "rejected") {
        navigate("/vendor-rejected");
        return;
      }
      fetchJobs();
    }
  }, [user]);

  const handleCreateOrUpdateJob = async (e) => {
    e.preventDefault();
    if (!formData.clientName || !formData.jobTitle || !formData.description) {
      toast({
        title: "Please fill all required fields",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("authToken");
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      if (isEditing && selectedJob) {
        const { data } = await axios.put(
          `/api/jobs/${selectedJob._id}`,
          formData,
          config
        );
        setJobs(jobs.map((job) => (job._id === data._id ? data : job)));
        toast({
          title: "Job updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "bottom",
        });
      } else {
        const { data } = await axios.post("/api/jobs", formData, config);
        setJobs([data, ...jobs]);
        toast({
          title: "Job created successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "bottom",
        });
      }

      resetForm();
      onClose();
    } catch (error) {
      toast({
        title: isEditing ? "Error updating job" : "Error creating job",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    }
    setSubmitting(false);
  };

  const resetForm = () => {
    setFormData({
      clientName: "",
      jobTitle: "",
      description: "",
      priority: "Medium",
      dueDate: "",
      location: "",
      estimatedCost: "",
      notes: "",
    });
    setIsEditing(false);
    setSelectedJob(null);
  };

  const handleViewJob = (job) => {
    setSelectedJob(job);
    onViewOpen();
  };

  const handleEditJob = (job) => {
    setSelectedJob(job);
    setIsEditing(true);
    setFormData({
      clientName: job.clientName,
      jobTitle: job.jobTitle,
      description: job.description,
      priority: job.priority,
      dueDate: job.dueDate ? job.dueDate.split("T")[0] : "",
      location: job.location || "",
      estimatedCost: job.estimatedCost || "",
      notes: job.notes || "",
    });
    onViewClose();
    onOpen();
  };

  const handleDeleteJob = async (jobId) => {
    if (window.confirm("Are you sure you want to delete this job?")) {
      try {
        const token = localStorage.getItem("authToken");
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
        await axios.delete(`/api/jobs/${jobId}`, config);
        setJobs(jobs.filter((job) => job._id !== jobId));
        toast({
          title: "Job deleted successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "bottom",
        });
        if (selectedJob && selectedJob._id === jobId) {
          onViewClose();
        }
      } catch (error) {
        toast({
          title: "Error deleting job",
          description: error.response?.data?.message || error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "bottom",
        });
      }
    }
  };

  const handleStatusChange = async (jobId, newStatus) => {
    try {
      const token = localStorage.getItem("authToken");
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      const { data } = await axios.put(
        `/api/jobs/${jobId}`,
        { status: newStatus },
        config
      );
      setJobs(jobs.map((job) => (job._id === data._id ? data : job)));

      if (selectedJob && selectedJob._id === jobId) {
        setSelectedJob(data);
      }
    } catch (error) {
      toast({
        title: "Error updating job status",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "Low":
        return "green";
      case "Medium":
        return "blue";
      case "High":
        return "red";
      default:
        return "gray";
    }
  };

  const getJobsByStatus = (status) => {
    return jobs.filter((job) => job.status === status);
  };

  const JobCard = ({ job }) => (
    <Box
      p={4}
      borderWidth="1px"
      borderRadius="lg"
      bg="white"
      shadow="sm"
      _hover={{ shadow: "md" }}
      mb={3}
    >
      <Flex justify="space-between" align="start" mb={2}>
        <VStack align="start" spacing={1} flex={1}>
          <Heading size="sm">{job.jobTitle}</Heading>
          <Text fontSize="sm" color="gray.600" fontWeight="medium">
            Client: {job.clientName}
          </Text>
        </VStack>
        <HStack>
          <IconButton
            icon={<EditIcon />}
            size="sm"
            colorScheme="blue"
            variant="ghost"
            onClick={() => handleEditJob(job)}
          />
          <IconButton
            icon={<DeleteIcon />}
            size="sm"
            colorScheme="red"
            variant="ghost"
            onClick={() => handleDeleteJob(job._id)}
          />
        </HStack>
      </Flex>

      <Text fontSize="sm" color="gray.600" noOfLines={2} mb={3}>
        {job.description}
      </Text>

      <HStack justify="space-between" mb={2}>
        <Badge colorScheme={getPriorityColor(job.priority)}>
          {job.priority}
        </Badge>
        {job.dueDate && (
          <Text fontSize="xs" color="gray.500">
            Due: {new Date(job.dueDate).toLocaleDateString()}
          </Text>
        )}
      </HStack>

      {job.location && (
        <Text fontSize="xs" color="gray.500" mb={2}>
          📍 {job.location}
        </Text>
      )}

      <Button
        size="sm"
        colorScheme="orange"
        variant="outline"
        width="100%"
        onClick={() => handleViewJob(job)}
      >
        View Details
      </Button>
    </Box>
  );

  const KanbanColumn = ({ title, status, jobs }) => (
    <Box
      bg="gray.50"
      p={4}
      borderRadius="lg"
      borderWidth="2px"
      borderColor={
        status === "To-Do"
          ? "blue.200"
          : status === "Doing"
          ? "orange.200"
          : "green.200"
      }
      display="flex"
      flexDirection="column"
      height="fit-content"
    >
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md" color="gray.700">
          {title}
        </Heading>
        <Badge
          colorScheme={
            status === "To-Do"
              ? "blue"
              : status === "Doing"
              ? "orange"
              : "green"
          }
          fontSize="lg"
          px={3}
          py={1}
          borderRadius="full"
        >
          {jobs.length}
        </Badge>
      </Flex>

      <Box
        maxH="calc(3 * 220px)"
        overflowY="auto"
        css={{
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#555",
          },
        }}
      >
        <VStack spacing={0} align="stretch">
          {jobs.length === 0 ? (
            <Text textAlign="center" color="gray.400" py={10}>
              No jobs in {title.toLowerCase()}
            </Text>
          ) : (
            jobs.map((job) => (
              <Box key={job._id}>
                <JobCard job={job} />
                <HStack spacing={2} mb={3}>
                  {status !== "To-Do" && (
                    <Button
                      size="xs"
                      colorScheme="blue"
                      variant="outline"
                      flex={1}
                      onClick={() =>
                        handleStatusChange(
                          job._id,
                          status === "Doing" ? "To-Do" : "Doing"
                        )
                      }
                    >
                      ← {status === "Doing" ? "To-Do" : "Doing"}
                    </Button>
                  )}
                  {status !== "Done" && (
                    <Button
                      size="xs"
                      colorScheme="green"
                      variant="outline"
                      flex={1}
                      onClick={() =>
                        handleStatusChange(
                          job._id,
                          status === "To-Do" ? "Doing" : "Done"
                        )
                      }
                    >
                      {status === "To-Do" ? "Doing" : "Done"} →
                    </Button>
                  )}
                </HStack>
              </Box>
            ))
          )}
        </VStack>
      </Box>
    </Box>
  );

  if (!user || user.role !== "vendor") {
    return (
      <Container maxW="container.md" py={10}>
        <Text>Only vendors can access this page.</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">My Jobs</Heading>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="orange"
          onClick={() => {
            resetForm();
            onOpen();
          }}
        >
          Add New Job
        </Button>
      </Flex>

      {loading ? (
        <Flex justify="center" align="center" minH="500px">
          <Spinner size="xl" color="orange.500" />
        </Flex>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <KanbanColumn
            title="To-Do"
            status="To-Do"
            jobs={getJobsByStatus("To-Do")}
          />
          <KanbanColumn
            title="Doing"
            status="Doing"
            jobs={getJobsByStatus("Doing")}
          />
          <KanbanColumn
            title="Done"
            status="Done"
            jobs={getJobsByStatus("Done")}
          />
        </SimpleGrid>
      )}

      {/* Create/Edit Job Modal */}
      <Modal
        isOpen={isOpen}
        onClose={() => {
          resetForm();
          onClose();
        }}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{isEditing ? "Edit Job" : "Add New Job"}</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleCreateOrUpdateJob}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Client Name</FormLabel>
                  <Input
                    placeholder="Enter client name"
                    value={formData.clientName}
                    onChange={(e) =>
                      setFormData({ ...formData, clientName: e.target.value })
                    }
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Job Title</FormLabel>
                  <Input
                    placeholder="e.g., Kitchen Renovation"
                    value={formData.jobTitle}
                    onChange={(e) =>
                      setFormData({ ...formData, jobTitle: e.target.value })
                    }
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    placeholder="Describe the job details"
                    rows={4}
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                  />
                </FormControl>

                <SimpleGrid columns={2} spacing={4} width="100%">
                  <FormControl>
                    <FormLabel>Priority</FormLabel>
                    <Select
                      value={formData.priority}
                      onChange={(e) =>
                        setFormData({ ...formData, priority: e.target.value })
                      }
                    >
                      {priorities.map((priority) => (
                        <option key={priority} value={priority}>
                          {priority}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Due Date</FormLabel>
                    <Input
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) =>
                        setFormData({ ...formData, dueDate: e.target.value })
                      }
                    />
                  </FormControl>
                </SimpleGrid>

                <FormControl>
                  <FormLabel>Location</FormLabel>
                  <Input
                    placeholder="Job location/address"
                    value={formData.location}
                    onChange={(e) =>
                      setFormData({ ...formData, location: e.target.value })
                    }
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Estimated Earnings ($)</FormLabel>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={formData.estimatedCost}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        estimatedCost: e.target.value,
                      })
                    }
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Notes</FormLabel>
                  <Textarea
                    placeholder="Additional notes"
                    rows={3}
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                  />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button
                variant="ghost"
                mr={3}
                onClick={() => {
                  resetForm();
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Button colorScheme="orange" type="submit" isLoading={submitting}>
                {isEditing ? "Update Job" : "Create Job"}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* View Job Details Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Job Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedJob && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontWeight="bold" mb={1}>
                    Job Title
                  </Text>
                  <Text fontSize="lg">{selectedJob.jobTitle}</Text>
                </Box>

                <Divider />

                <Box>
                  <Text fontWeight="bold" mb={1}>
                    Client Name
                  </Text>
                  <Text>{selectedJob.clientName}</Text>
                </Box>

                <Divider />

                <Box>
                  <Text fontWeight="bold" mb={1}>
                    Description
                  </Text>
                  <Text whiteSpace="pre-wrap">{selectedJob.description}</Text>
                </Box>

                <Divider />

                <SimpleGrid columns={3} spacing={4}>
                  <Box>
                    <Text fontWeight="bold" mb={1} fontSize="sm">
                      Status
                    </Text>
                    <Badge
                      colorScheme={
                        selectedJob.status === "To-Do"
                          ? "blue"
                          : selectedJob.status === "Doing"
                          ? "orange"
                          : "green"
                      }
                      fontSize="md"
                    >
                      {selectedJob.status}
                    </Badge>
                  </Box>

                  <Box>
                    <Text fontWeight="bold" mb={1} fontSize="sm">
                      Priority
                    </Text>
                    <Badge
                      colorScheme={getPriorityColor(selectedJob.priority)}
                      fontSize="md"
                    >
                      {selectedJob.priority}
                    </Badge>
                  </Box>

                  {selectedJob.dueDate && (
                    <Box>
                      <Text fontWeight="bold" mb={1} fontSize="sm">
                        Due Date
                      </Text>
                      <Text fontSize="sm">
                        {new Date(selectedJob.dueDate).toLocaleDateString()}
                      </Text>
                    </Box>
                  )}
                </SimpleGrid>

                {selectedJob.location && (
                  <>
                    <Divider />
                    <Box>
                      <Text fontWeight="bold" mb={1}>
                        Location
                      </Text>
                      <Text>📍 {selectedJob.location}</Text>
                    </Box>
                  </>
                )}

                {selectedJob.estimatedCost && (
                  <>
                    <Divider />
                    <Box>
                      <Text fontWeight="bold" mb={1}>
                        Estimated Earnings
                      </Text>
                      <Text fontSize="lg" color="green.600" fontWeight="bold">
                        ${selectedJob.estimatedCost}
                      </Text>
                    </Box>
                  </>
                )}

                {selectedJob.notes && (
                  <>
                    <Divider />
                    <Box>
                      <Text fontWeight="bold" mb={1}>
                        Notes
                      </Text>
                      <Text whiteSpace="pre-wrap">{selectedJob.notes}</Text>
                    </Box>
                  </>
                )}

                <Divider />

                <Box>
                  <Text fontWeight="bold" mb={1} fontSize="sm">
                    Created
                  </Text>
                  <Text fontSize="sm">
                    {new Date(selectedJob.createdAt).toLocaleString()}
                  </Text>
                </Box>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={() => handleEditJob(selectedJob)}
            >
              Edit Job
            </Button>
            <Button colorScheme="orange" onClick={onViewClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default VendorJobsPage;
